import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  aprovarPedidoCompra,
  cancelPedidoCompra,
  createStockEntry,
  gerarLinkPedidoCompra,
  getAllSuppliers,
  getPedidoCompra,
  getPedidosCompra,
  invalidarLinkPedidoCompra,
  marcarRecebidoPedidoCompra,
} from '../services/api';
import { getAuthPermissoes } from '../services/authStorage';
import { getApiErrorMessage } from '../utils/apiError';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';
import { useEstabelecimentoFiltroApi } from '../context/EstabelecimentoContext';

const STATUS_LIST = ['Rascunho', 'Enviado', 'Respondido', 'Aprovado', 'Recebido', 'Cancelado'];

const STATUS_COLOR = {
  Rascunho: 'default',
  Enviado: 'info',
  Respondido: 'warning',
  Aprovado: 'success',
  Recebido: 'primary',
  Cancelado: 'error',
};

const money = (value) =>
  Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const fmtDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR');
};

const fmtQtd = (n) => {
  const x = Number(n) || 0;
  return Number.isInteger(x) ? String(x) : x.toFixed(3);
};

const nomeFornecedor = (f) => f?.nomeFantasia || f?.razaoSocial || (f?.id ? `Fornecedor ${f.id}` : '');

const copiar = async (texto) => {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    return false;
  }
};

export default function PedidoCompraList() {
  const navigate = useNavigate();
  const estabelecimentoId = useEstabelecimentoFiltroApi();
  const permissions = useMemo(() => getAuthPermissoes() || {}, []);
  const can = useCallback((key) => Boolean(permissions?.[key]), [permissions]);
  const podeMovimentar = can('movimentaEstoque');

  const emptyFilters = useCallback(
    () => ({ status: '', termo: '', fornecedorId: '' }),
    [],
  );
  const [filters, setFilters] = useState(emptyFilters);
  const [applied, setApplied] = useState(emptyFilters);
  const [fornecedorSel, setFornecedorSel] = useState(null);
  const [fornecedorOpts, setFornecedorOpts] = useState([]);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], totalPages: 1, totalItems: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);

  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const [linkInfo, setLinkInfo] = useState(null); // { codigo, url, codigoAcesso, expiraEm }

  // Recebimento
  const [recv, setRecv] = useState(null); // { doc, itens: [{itemId, produtoId, ..., qtd}] }
  const [recvOpen, setRecvOpen] = useState(false);
  const [recvEntrouIds, setRecvEntrouIds] = useState([]);

  const load = useCallback(async () => {
    if (!can('pedidoCompra')) {
      setError('Sem permissão para pedidos de compra.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await getPedidosCompra({
        ...applied,
        estabelecimentoId: estabelecimentoId || '',
        indice: page,
        qtde: 20,
      });
      const payload = response.data || {};
      const totalItems = payload.totalItems || 0;
      const pageSize = payload.pageSize || 20;
      setData({
        items: payload.items || [],
        totalItems,
        totalPages: Math.max(1, Math.ceil(totalItems / pageSize)),
      });
    } catch (e) {
      setError(getApiErrorMessage(e, 'Não foi possível carregar os pedidos de compra.'));
    } finally {
      setLoading(false);
    }
  }, [applied, page, can, estabelecimentoId]);

  useEffect(() => {
    load();
  }, [load]);

  const buscarFornecedores = useCallback(async (termo) => {
    try {
      const { data: d } = await getAllSuppliers({ indice: 1, qtde: 15, termo: (termo || '').trim() });
      setFornecedorOpts(d?.items || (Array.isArray(d) ? d : []));
    } catch {
      setFornecedorOpts([]);
    }
  }, []);

  useEffect(() => {
    buscarFornecedores('');
  }, [buscarFornecedores]);

  const aplicarFiltros = (next = filters) => {
    setPage(1);
    setApplied(next);
  };

  const openDetail = async (rowId) => {
    setDetailOpen(true);
    setDetail(null);
    setBusy(true);
    try {
      const { data: d } = await getPedidoCompra(rowId);
      setDetail(d);
    } catch (e) {
      setError(getApiErrorMessage(e, 'Erro ao carregar detalhe.'));
      setDetailOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const run = async (fn, fallback) => {
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (e) {
      setError(getApiErrorMessage(e, fallback));
    } finally {
      setBusy(false);
    }
  };

  const handleGerarLink = (row) => {
    if (row.temLinkAtivo
      && !window.confirm('Já existe um link ativo. Gerar um novo invalida o anterior. Continuar?')) {
      return;
    }
    run(async () => {
      const { data: r } = await gerarLinkPedidoCompra(row.id);
      setLinkInfo({
        codigo: row.codigo,
        url: `${window.location.origin}${r.urlPath}`,
        codigoAcesso: r.codigoAcesso,
        expiraEm: r.expiraEm,
      });
      await load();
    }, 'Não foi possível gerar o link.');
  };

  const handleInvalidarLink = (row) => {
    if (!window.confirm(`Invalidar o link do pedido ${row.codigo}?`)) return;
    run(async () => {
      await invalidarLinkPedidoCompra(row.id);
      setInfo(`Link do pedido ${row.codigo} invalidado.`);
      await load();
    }, 'Não foi possível invalidar o link.');
  };

  const handleAprovar = (row) => {
    if (!window.confirm(`Aprovar o pedido ${row.codigo}?`)) return;
    run(async () => {
      await aprovarPedidoCompra(row.id);
      setDetailOpen(false);
      await load();
    }, 'Não foi possível aprovar.');
  };

  const handleCancelar = (row) => {
    if (!window.confirm(`Cancelar o pedido ${row.codigo}?`)) return;
    run(async () => {
      await cancelPedidoCompra(row.id, { motivo: 'Cancelado no Gestão' });
      setDetailOpen(false);
      await load();
    }, 'Não foi possível cancelar.');
  };

  const openReceber = (row) => {
    setRecvEntrouIds([]);
    setRecvOpen(true);
    setRecv(null);
    run(async () => {
      try {
        const { data: d } = await getPedidoCompra(row.id);
        setRecv({
          doc: d,
          itens: (d.itens || []).map((i) => ({
            itemId: i.id,
            produtoId: i.produtoId,
            produtoCodigo: i.produtoCodigo,
            produtoDescricao: i.produtoDescricao,
            qtdPedida: Number(i.qtdPedida || 0),
            custoUnitario: Number(i.custoUnitario || 0),
            qtd: Number(i.qtdAtendida) > 0 ? Number(i.qtdAtendida) : Number(i.qtdPedida || 0),
          })),
        });
      } catch (e) {
        setRecvOpen(false);
        throw e;
      }
    }, 'Erro ao carregar pedido para recebimento.');
  };

  const setRecvQtd = (idx, value) =>
    setRecv((prev) => ({
      ...prev,
      itens: prev.itens.map((it, i) => (i === idx ? { ...it, qtd: value } : it)),
    }));

  const confirmarRecebimento = async () => {
    if (!recv?.doc) return;
    if (!podeMovimentar) {
      setError('Sem permissão para movimentar estoque.');
      return;
    }
    const itens = recv.itens.map((i) => ({ ...i, qtdNum: Number(String(i.qtd).replace(',', '.')) }));
    if (itens.some((i) => !Number.isFinite(i.qtdNum) || i.qtdNum < 0)) {
      setError('Quantidade recebida inválida.');
      return;
    }
    const aEntrar = itens.filter((i) => i.qtdNum > 0 && !recvEntrouIds.includes(i.produtoId));
    if (aEntrar.length === 0 && recvEntrouIds.length === 0) {
      setError('Informe ao menos um item com quantidade recebida maior que zero.');
      return;
    }
    if (!window.confirm(
      `Confirmar o recebimento de ${recv.doc.codigo}? A entrada será lançada no estoque.`,
    )) return;

    setBusy(true);
    setError('');
    try {
      if (aEntrar.length > 0) {
        const payload = aEntrar.map((i) => ({
          produtoId: i.produtoId,
          quantidade: i.qtdNum,
          estabelecimentoId: Number(recv.doc.estabelecimentoId),
          localEstoqueId: 0,
          custoUnitario: i.custoUnitario,
          atualizarCusto: true,
          tipoId: 1,
          referencia: recv.doc.codigo,
          observacao: `Recebimento do pedido de compra ${recv.doc.codigo}`,
        }));
        const { data: res } = await createStockEntry(payload);
        const falhas = res?.errosDetalhe || [];
        if ((res?.erros || 0) > 0 || falhas.length > 0) {
          const falhouIds = falhas.map((f) => f.produtoId);
          setRecvEntrouIds((prev) => [
            ...prev,
            ...aEntrar.map((i) => i.produtoId).filter((pid) => !falhouIds.includes(pid)),
          ]);
          setError(
            `Entrada parcial. Falhas: ${falhas.map((f) => f.mensagem).join('; ') || 'verifique os itens'}. `
            + 'Os itens já lançados não serão repetidos ao tentar novamente.',
          );
          return;
        }
      }
      try {
        await marcarRecebidoPedidoCompra(recv.doc.id, {
          itens: itens.map((i) => ({ itemId: i.itemId, qtdAtendida: i.qtdNum })),
        });
      } catch (e) {
        setRecvEntrouIds(itens.filter((i) => i.qtdNum > 0).map((i) => i.produtoId));
        setError(
          `Estoque lançado, mas não foi possível marcar como recebido: ${getApiErrorMessage(e, 'tente novamente')}. `
          + 'Clique em confirmar novamente (a entrada não será duplicada).',
        );
        return;
      }
      setRecvOpen(false);
      setInfo(`Pedido ${recv.doc.codigo} recebido e entrada lançada no estoque.`);
      await load();
    } catch (e) {
      setError(getApiErrorMessage(e, 'Não foi possível lançar a entrada de estoque.'));
    } finally {
      setBusy(false);
    }
  };

  const podeEditar = (s) => s === 'Rascunho' || s === 'Respondido';
  const podeGerarLink = (s) => s === 'Rascunho' || s === 'Enviado' || s === 'Respondido';
  const podeAprovar = (s) => s === 'Rascunho' || s === 'Enviado' || s === 'Respondido';
  const podeCancelar = (s) => s !== 'Recebido' && s !== 'Cancelado';

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Pedidos de compra"
        subtitle="Monte o pedido, envie o link ao fornecedor, aprove e dê entrada no estoque ao receber."
        actions={(
          <Button
            variant="contained"
            onClick={() => navigate('/pedidos-compra/novo')}
            disabled={!can('pedidoCompra')}
          >
            Novo pedido de compra
          </Button>
        )}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
          <TextField
            select
            label="Status"
            size="small"
            value={filters.status}
            onChange={(e) => {
              const next = { ...filters, status: e.target.value };
              setFilters(next);
              aplicarFiltros(next);
            }}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="">Todos</MenuItem>
            {STATUS_LIST.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </TextField>
          <Autocomplete
            size="small"
            sx={{ minWidth: 260 }}
            options={fornecedorOpts}
            value={fornecedorSel}
            getOptionLabel={nomeFornecedor}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            filterOptions={(x) => x}
            noOptionsText="Nenhum fornecedor"
            onInputChange={(_, v, reason) => {
              if (reason === 'input') buscarFornecedores(v);
            }}
            onChange={(_, v) => {
              setFornecedorSel(v);
              const next = { ...filters, fornecedorId: v?.id ? String(v.id) : '' };
              setFilters(next);
              aplicarFiltros(next);
            }}
            renderInput={(params) => <TextField {...params} label="Fornecedor (opcional)" />}
          />
          <TextField
            label="Busca"
            size="small"
            placeholder="PC000123, fornecedor…"
            value={filters.termo}
            onChange={(e) => setFilters((p) => ({ ...p, termo: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') aplicarFiltros();
            }}
            sx={{ minWidth: 220 }}
          />
          <Button variant="contained" disabled={loading} onClick={() => aplicarFiltros()}>
            Filtrar
          </Button>
        </Stack>
      </Paper>

      {(loading || busy) && <LinearProgress />}
      <ResponsiveTable>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Código</TableCell>
              <TableCell>Fornecedor</TableCell>
              <TableCell>Data</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Link</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data.items || []).map((row) => (
              <TableRow key={row.id} hover>
                <TableCell data-label="Código">
                  <Typography fontWeight={800}>{row.codigo}</Typography>
                </TableCell>
                <TableCell data-label="Fornecedor">
                  <Typography variant="body2" fontWeight={700}>{row.fornecedorNome}</Typography>
                  {row.fornecedorDocumento && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {row.fornecedorDocumento}
                    </Typography>
                  )}
                </TableCell>
                <TableCell data-label="Data">{fmtDate(row.dataCriacao)}</TableCell>
                <TableCell data-label="Total" align="right">{money(row.valorTotal)}</TableCell>
                <TableCell data-label="Status">
                  <Chip size="small" label={row.status} color={STATUS_COLOR[row.status] || 'default'} />
                </TableCell>
                <TableCell data-label="Link">
                  {row.temLinkAtivo ? (
                    <Chip size="small" color="success" variant="outlined" icon={<LinkIcon />} label="Link ativo" />
                  ) : (
                    <Typography variant="caption" color="text.secondary">—</Typography>
                  )}
                </TableCell>
                <TableCell align="right" data-actions="" onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="Detalhe">
                    <IconButton color="primary" onClick={() => openDetail(row.id)}>
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  {podeEditar(row.status) && (
                    <Tooltip title="Editar">
                      <IconButton
                        color="primary"
                        onClick={() => navigate(`/pedidos-compra/${row.id}/editar`)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {podeGerarLink(row.status) && (
                    <Tooltip title="Gerar link para o fornecedor">
                      <IconButton color="info" disabled={busy} onClick={() => handleGerarLink(row)}>
                        <LinkIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {row.temLinkAtivo && (
                    <Tooltip title="Invalidar link">
                      <IconButton color="warning" disabled={busy} onClick={() => handleInvalidarLink(row)}>
                        <LinkOffIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {podeAprovar(row.status) && (
                    <Tooltip title="Aprovar">
                      <IconButton color="success" disabled={busy} onClick={() => handleAprovar(row)}>
                        <CheckCircleOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {row.status === 'Aprovado' && (
                    <Tooltip title={podeMovimentar ? 'Receber (entrada no estoque)' : 'Sem permissão de movimentar estoque'}>
                      <span>
                        <IconButton
                          color="primary"
                          disabled={busy || !podeMovimentar}
                          onClick={() => openReceber(row)}
                        >
                          <MoveToInboxIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {podeCancelar(row.status) && (
                    <Tooltip title="Cancelar">
                      <IconButton color="error" disabled={busy} onClick={() => handleCancelar(row)}>
                        <CancelOutlinedIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!loading && (data.items || []).length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                    Nenhum pedido de compra encontrado.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ResponsiveTable>

      {(data.totalPages || 1) > 1 && (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Pagination
            page={page}
            count={data.totalPages || 1}
            onChange={(_, value) => setPage(value)}
          />
        </Stack>
      )}

      {/* Detalhe */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {detail?.codigo || 'Detalhe'}{detail ? ` · ${detail.status}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {!detail ? (
            <LinearProgress />
          ) : (
            <Stack spacing={1.25}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="caption" color="text.secondary">Fornecedor</Typography>
                <Typography fontWeight={800}>{detail.fornecedorNome}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {[detail.fornecedorDocumento, detail.fornecedorTelefone, detail.fornecedorEmail]
                    .filter(Boolean).join(' · ') || 'Sem dados cadastrais'}
                </Typography>
              </Paper>
              <Typography variant="body2">
                Criado em {fmtDate(detail.dataCriacao)} por {detail.usuarioCriacao || '—'}
                {detail.respondidoEm ? ` · Respondido em ${fmtDate(detail.respondidoEm)}` : ''}
                {detail.dataAprovacao ? ` · Aprovado em ${fmtDate(detail.dataAprovacao)}` : ''}
                {detail.dataRecebimento ? ` · Recebido em ${fmtDate(detail.dataRecebimento)}` : ''}
              </Typography>
              {detail.observacao ? (
                <Typography variant="body2">Observação: {detail.observacao}</Typography>
              ) : null}
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Produto</TableCell>
                    <TableCell align="right">Pedida</TableCell>
                    <TableCell align="right">Atendida</TableCell>
                    <TableCell align="right">Custo</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(detail.itens || []).map((i) => (
                    <TableRow key={i.id}>
                      <TableCell>
                        {i.produtoCodigo} · {i.produtoDescricao}
                        {i.observacao ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {i.observacao}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell align="right">{fmtQtd(i.qtdPedida)}</TableCell>
                      <TableCell align="right">{fmtQtd(i.qtdAtendida)}</TableCell>
                      <TableCell align="right">{money(i.custoUnitario)}</TableCell>
                      <TableCell align="right">{money(i.valorTotal)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography align="right" fontWeight={800}>Total: {money(detail.valorTotal)}</Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          {detail && podeEditar(detail.status) && (
            <Button
              startIcon={<EditIcon />}
              onClick={() => navigate(`/pedidos-compra/${detail.id}/editar`)}
            >
              Editar
            </Button>
          )}
          {detail && podeAprovar(detail.status) && (
            <Button
              color="success"
              startIcon={<CheckCircleOutlineIcon />}
              disabled={busy}
              onClick={() => handleAprovar(detail)}
            >
              Aprovar
            </Button>
          )}
          {detail && podeCancelar(detail.status) && (
            <Button
              color="error"
              startIcon={<CancelOutlinedIcon />}
              disabled={busy}
              onClick={() => handleCancelar(detail)}
            >
              Cancelar pedido
            </Button>
          )}
          <Button onClick={() => setDetailOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Link gerado */}
      <Dialog open={Boolean(linkInfo)} onClose={() => setLinkInfo(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Link para o fornecedor · {linkInfo?.codigo}</DialogTitle>
        <DialogContent dividers>
          {linkInfo && (
            <Stack spacing={2}>
              <Alert severity="warning">
                O código de acesso só é exibido agora. Anote ou copie antes de fechar.
              </Alert>
              <TextField
                label="Link"
                size="small"
                fullWidth
                value={linkInfo.url}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <Tooltip title="Copiar link">
                      <IconButton onClick={async () => {
                        setInfo((await copiar(linkInfo.url)) ? 'Link copiado.' : 'Não foi possível copiar.');
                      }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
              />
              <TextField
                label="Código de acesso"
                size="small"
                fullWidth
                value={linkInfo.codigoAcesso}
                InputProps={{
                  readOnly: true,
                  sx: { fontWeight: 800, letterSpacing: 4 },
                  endAdornment: (
                    <Tooltip title="Copiar código">
                      <IconButton onClick={async () => {
                        setInfo((await copiar(linkInfo.codigoAcesso)) ? 'Código copiado.' : 'Não foi possível copiar.');
                      }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
              />
              <Typography variant="body2" color="text.secondary">
                Válido até {fmtDate(linkInfo.expiraEm)}.
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={async () => {
              const txt = `Pedido de compra ${linkInfo.codigo}\nLink: ${linkInfo.url}\nCódigo de acesso: ${linkInfo.codigoAcesso}`;
              setInfo((await copiar(txt)) ? 'Link e código copiados.' : 'Não foi possível copiar.');
            }}
          >
            Copiar tudo
          </Button>
          <Button variant="contained" onClick={() => setLinkInfo(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Recebimento */}
      <Dialog
        open={recvOpen}
        onClose={() => !busy && setRecvOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Receber pedido {recv?.doc?.codigo || ''}</DialogTitle>
        <DialogContent dividers>
          {!recv ? (
            <LinearProgress />
          ) : (
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                Fornecedor: <strong>{recv.doc.fornecedorNome}</strong>. Ajuste a quantidade
                efetivamente recebida; a entrada será lançada no estoque (e o custo atualizado)
                antes de marcar o pedido como Recebido.
              </Typography>
              {error && (
                <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
              )}
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Produto</TableCell>
                    <TableCell align="right">Pedida</TableCell>
                    <TableCell align="right">Custo</TableCell>
                    <TableCell align="right">Qtd recebida</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recv.itens.map((it, idx) => (
                    <TableRow key={it.itemId}>
                      <TableCell>
                        {it.produtoCodigo} · {it.produtoDescricao}
                        {recvEntrouIds.includes(it.produtoId) && (
                          <Chip size="small" color="success" label="já lançado" sx={{ ml: 1 }} />
                        )}
                      </TableCell>
                      <TableCell align="right">{fmtQtd(it.qtdPedida)}</TableCell>
                      <TableCell align="right">{money(it.custoUnitario)}</TableCell>
                      <TableCell align="right">
                        <TextField
                          size="small"
                          type="number"
                          value={it.qtd}
                          disabled={busy || recvEntrouIds.includes(it.produtoId)}
                          onChange={(e) => setRecvQtd(idx, e.target.value)}
                          inputProps={{ min: 0, step: 'any' }}
                          sx={{ width: 120 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecvOpen(false)} disabled={busy}>Fechar</Button>
          <Button
            variant="contained"
            startIcon={<MoveToInboxIcon />}
            disabled={busy || !recv}
            onClick={confirmarRecebimento}
          >
            {busy ? 'Processando…' : 'Confirmar recebimento'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
